import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { createOrUpdateBorrower } from '@/apiCalls/borrower';
import { Button } from '@/components/Button';
import { ModalComponent } from '@/components/ModalComponent';
import { ModalHeader } from '@/components/ModalHeader';
import {
    EBorrowerCitizenshipStatus,
    selectAddressByIndex,
    selectEmail,
    selectFirstName,
    selectLastName,
    selectLicenseNumber,
    selectLicenseState,
    selectMobile,
    selectResidencyStatus,
    selectVisaNumber,
} from '@/context/borrower';
import {
    EJourneys,
    selectInitialJourney,
    selectIsNZ,
    setModal,
} from '@/context/display';
import { RootState } from '@/context/store';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';
import { LivingStatus } from '@/UserInput';
import { ResidencyStatus } from '@/UserInput/ResidencyStatus';
import { isBirthDateValid } from '@/utils/checkBirthDate';
import { isLicenseExpiryDateValid } from '@/utils/checkLicenseExpiryDate';
import { isVisaExpiryDateValid } from '@/utils/checkVisaExpiryDate';
import { getOcrFields } from '@/utils/getOcrFields';
import { isEmail } from '@/utils/isEmail';
import { isMobile } from '@/utils/isMobile';

export const testId = 'OcrCheckIncomplete';
export function OcrCheckIncomplete(): JSX.Element {
    // ***** Constants *****
    const baseId = 'modal-ocrSummaryCheck';
    // ***** Local State *****
    const [showErrors, setShowErrors] = useState<boolean>(false);
    const [missingFields, setMissingFields] = useState<JSX.Element[]>([]);
    const [missingFieldsFullWidth, setMissingFieldsFullWidth] = useState<
        JSX.Element[]
    >([]);

    // ***** Redux State *****
    const dispatch = useAppDispatch();
    // Contact Details
    const firstName = useAppSelector(selectFirstName);
    const lastName = useAppSelector(selectLastName);
    const email = useAppSelector(selectEmail);
    // Personal Information
    const licenseNumber = useAppSelector(selectLicenseNumber);
    const licenseState = useAppSelector(selectLicenseState);
    // const licenseVersionNumber = useAppSelector(selectLicenseVersionNumber); // Optional for NZ
    const visaNumber = useAppSelector(selectVisaNumber);
    const phone = useAppSelector(selectMobile);
    const residencyStatus = useAppSelector(selectResidencyStatus);
    const initialJourney = useAppSelector(selectInitialJourney);
    const isTestDrive = initialJourney === EJourneys.TEST_DRIVE;
    const isNZ = useAppSelector(selectIsNZ);

    // Address Information
    const address = useSelector((state: RootState) =>
        selectAddressByIndex(state, 0)
    );
    const { ownershipType, streetNumber, streetName, suburb, state, postcode } =
        address;

    // ***** UseEffect *****
    useEffect(() => {
        checkMissingFields();
    }, []);

    // ***** Handlers *****
    async function handleClick(): Promise<void> {
        setShowErrors(true);
        if (!isComplete()) return;
        try {
            await createOrUpdateBorrower();
            dispatch(setModal(null));
        } catch (error) {
            console.error('Error saving borrower data:', error);
            // Don't close modal if there's an error
        }
    }

    // ***** Helpers *****
    function isComplete(): boolean {
        // Contact Details
        if (firstName === '' || firstName === undefined) return false;
        if (lastName === '' || lastName === undefined) return false;
        if (!isEmail(email)) return false;

        // Not required for test drive flow
        if (!isTestDrive) {
            // Phone is required for non-test drive flows
            if (!phone || phone === '' || !isMobile(phone)) return false;
            if (ownershipType === null || ownershipType === undefined)
                return false;

            if (
                residencyStatus === EBorrowerCitizenshipStatus.VISA_HOLDER &&
                !isNZ &&
                (visaNumber === undefined ||
                    visaNumber === 0 ||
                    !isVisaExpiryDateValid())
            )
                return false;

            if (residencyStatus === null || residencyStatus === undefined)
                return false;
        }
        // Personal Information
        if (!isBirthDateValid()) return false;

        // For NZ, licenseVersionNumber is optional; for AU, check licenseNumber
        if (isNZ) {
            // For NZ, licenseVersionNumber is optional - no validation needed
            // For NZ, license state and expiry are optional
        } else {
            if (licenseNumber === '' || licenseNumber === undefined)
                return false;
            // For AU, license state and expiry are required
            if (licenseState === '' || licenseState === undefined) return false;
            if (!isLicenseExpiryDateValid()) return false;
        }
        // Address Information
        if (streetNumber === '' || streetNumber === undefined) return false;
        if (streetName === '' || streetName === undefined) return false;
        if (suburb === '' || suburb === undefined) return false;
        if (state === null || state === undefined) return false;
        if (
            postcode === '' ||
            postcode === undefined ||
            postcode.toString().length < 4
        )
            return false;

        return true;
    }

    const checkMissingFields = (): void => {
        const missing: JSX.Element[] = [];
        const missingFullWidth: JSX.Element[] = [];
        getOcrFields(baseId, showErrors).map((item) => {
            // Check if item is a LivingStatus component
            const isLivingStatus =
                React.isValidElement(item) && item.type === LivingStatus;
            const isResidencyStatus =
                React.isValidElement(item) && item.type === ResidencyStatus;

            // Skip LivingStatus for test drive
            if (isLivingStatus && isTestDrive) return;

            // Skip ResidencyStatus for test drive
            if (isResidencyStatus && isTestDrive) return;

            if (isLivingStatus || isResidencyStatus) {
                missingFullWidth.push(item);
                return;
            }
            missing.push(item);
        });
        setMissingFieldsFullWidth(missingFullWidth);
        setMissingFields(missing);
    };

    // ***** Render *****
    return (
        <>
            <ModalComponent
                baseId={baseId}
                className='overflow-y-scroll w-full h-full'
                data-testid={testId}
            >
                <ModalHeader title='Please fill to proceed' baseId={baseId} />
                <div
                    className={`${
                        missingFields.length > 1 ? 'grid grid-cols-2 gap-4' : ''
                    }`}
                >
                    {missingFieldsFullWidth.map((item, index) => {
                        return (
                            <div
                                key={index}
                                className={`min-w-0 flex-shrink col-span-2`}
                            >
                                {React.cloneElement(item, {
                                    showErrors: showErrors,
                                })}
                            </div>
                        );
                    })}
                    {missingFields.map((item, index) => {
                        return (
                            <div key={index} className={`min-w-0 flex-shrink`}>
                                {React.cloneElement(item, {
                                    showErrors: showErrors,
                                })}
                            </div>
                        );
                    })}
                </div>
                <div className='mt-4 mx-16'>
                    <Button onClick={handleClick} id={`${baseId}-button-close`}>
                        Save
                    </Button>
                </div>
            </ModalComponent>
        </>
    );
}
